/**
 * @file SignUp.tsx
 * @description Component for user sign-up, integrating with Firebase Authentication.
 */

import React, { useState } from "react";
import GoogleAuthButton from "./GoogleAuthButton";
import log from "../../config/logging";
import { useUserDetails } from "../../context/UserDetailsContext";
import PrivacyConsentCheckbox from "../common/PrivacyConsentCheckbox";

const SignUp: React.FC<{
  onSuccess: () => void;
  switchToLogin: () => void;
}> = ({ onSuccess, switchToLogin }) => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [privacyConsent, setPrivacyConsent] = useState<boolean>(false);
  const { signUpWithEmailPassword, signInWithGoogle } = useUserDetails();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!privacyConsent) {
      setError(
        "È necessario accettare l'informativa sulla privacy per procedere."
      );
      return;
    }
    setLoading(true);
    setError("");

    try {
      await signUpWithEmailPassword(
        email,
        password,
        firstName,
        lastName,
        privacyConsent
      );
      onSuccess();
    } catch (error) {
      log.error("Sign-up error", { error });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      await signInWithGoogle();
      onSuccess();
    } catch (error) {
      log.error("Google sign-up error", { error });
    }
  };

  const handleSwitchToLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    switchToLogin();
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="flex flex-col">
        <input
          type="text"
          placeholder="Nome"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
          className="w-full mb-4 text-center normal-case border border-gray-300 rounded-full bg-base-100 input text-primary sm:min-w-60"
        />
        <input
          type="text"
          placeholder="Cognome"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          required
          className="w-full mb-4 text-center normal-case border border-gray-300 rounded-full bg-base-100 input text-primary sm:min-w-60"
        />
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full mb-4 text-center normal-case border border-gray-300 rounded-full bg-base-100 input text-primary sm:min-w-60"
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="w-full mb-4 text-center normal-case border border-gray-300 rounded-full bg-base-100 input text-primary sm:min-w-60"
        />
        <PrivacyConsentCheckbox
          checked={privacyConsent}
          onChange={(checked: boolean) => setPrivacyConsent(checked)}
        />
        <button
          type="submit"
          className="px-6 mb-4 text-lg font-bold border-none rounded-full text-formbutton text-primary-content bg-primary btn"
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="loading loading-spinner"></span> Registrazione in
              corso…
            </>
          ) : (
            "Registrati"
          )}
        </button>
        <p className="my-4 text-center text-neutral">
          Hai già un account?{" "}
          <button
            type="button"
            className="underline text-primary"
            onClick={handleSwitchToLogin}
          >
            Accedi
          </button>
        </p>
        <div className="mb-8 uppercase divider text-neutral">Oppure</div>
        <GoogleAuthButton onSuccess={handleGoogleSignUp} />
        {error && <p className="mt-4 text-center text-error">{error}</p>}
      </form>
    </>
  );
};

export default SignUp;
